import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faFilePdf} from '@fortawesome/fontawesome-pro-regular';
import {CoverageSummaryModal} from './CoverageSummaryModal';
import {sanitizeJsonString} from '../../libs/salmon-utils';
import {faFile} from '@fortawesome/fontawesome-pro-solid';
import api from 'libs/api';
import {triStateDisplay} from './table-formatter';

/**
 * @deprecated Use CoverageSummary from ./CoverageSummary/index.jsx instead
 * Wrapper component to handle state for the coverage summary modal
 */
const CoverageSummary = ({handler = 'modal', rowData, settings, styleSheet}) => {
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [localPdfUrl, setLocalPdfUrl] = useState(null);

  // Effect to open PDF URL when it becomes available
  useEffect(() => {
    if (localPdfUrl) {
      window.open(localPdfUrl, '_blank');
      setIsGenerating(false); // Reset local loading state when PDF is ready
      // Reset the URL after opening to prevent reopening on re-renders
      setLocalPdfUrl(null);
    }
  }, [localPdfUrl]);

  const handleCoverageSummaryModal = (e) => {
    e.preventDefault();
    setShowSummaryModal(true);
  };

  // Handler for PDF export
  const handlePdfExport = (e) => {
    e.preventDefault();
    // Prevent multiple clicks
    if (isGenerating) {
      return;
    }

    // Set local loading state
    setIsGenerating(true);

    // Direct API call without Redux
    try {
      const pdfData = {
        templateId: settings.pdf?.coverageSummaryTemplateId,
        name: `coverage-summary-${rowData.id}`,
        data: mapCoverageSummaryPdfData(rowData, settings.pdf?.metaDataField)
      };

      api.post(`/pdf/${settings.name}`, pdfData)
        .then((responseData) => {
          // Set the local PDF URL when the API call succeeds
          setLocalPdfUrl(responseData.data.response);
        })
        .catch((error) => {
          console.error('Error generating PDF:', error);
          setIsGenerating(false);
        });
    } catch (error) {
      console.error('Error generating PDF:', error);
      setIsGenerating(false);
    }
  };

  switch (handler) {
    case 'pdfLink':
      return (
        <a
          id={rowData.id}
          onClick={handlePdfExport}
          data-testid={`coverage-summary-link-${rowData.id}`}
          style={{cursor: 'pointer'}}
          title="Export as PDF"
        >
          <FontAwesomeIcon icon={faFilePdf} size={'2x'} />
          {isGenerating && <span style={{marginLeft: '5px'}}>Generating...</span>}
        </a>
      );
    case 'modal':
    default:
      return (
        <>
          <a id={rowData.id} onClick={handleCoverageSummaryModal} data-testid={`coverage-summary-link-${rowData.id}`}><FontAwesomeIcon icon={faFile} size={'2x'} /></a>
          <CoverageSummaryModal
            settings={settings}
            styleSheet={styleSheet}
            showSummaryModal={showSummaryModal}
            handleHide={() => setShowSummaryModal(false)}
            handleDocumentClick={(e) => e.preventDefault()}
            rowData={rowData.coverageSummaryModalData}
          />
        </>
      );
  }
};

CoverageSummary.propTypes = {
  handler: PropTypes.string,
  rowData: PropTypes.object.isRequired,
  settings: PropTypes.object.isRequired,
  styleSheet: PropTypes.object.isRequired,
};

export default CoverageSummary;

/**
 * Maps the coverage summary data to the PDF data format.
 *
 * @param {Object} rowData - The row data containing coverage summary information
 * @param {string} metaDataField - The field name containing metadata
 * @returns {Object} The mapped PDF data
 */
const mapCoverageSummaryPdfData = (rowData, metaDataField) => {
  const {
    payer,
    lastReviewDate,
    product,
    indications,
    planType,
    states,
    covered,
    documentUrl,
  } = rowData;

  let pdfData = {
    refresh_date: lastReviewDate,
    product_name: product,
    indication: indications.join(', '),
    payer,
    payer_phone: null,
    payer_fax: null,
    plan_type: planType,
    states: states.join(', '),
    covered,
    diagnosis_codes: null,
    symptom_duration: null,
    pharma_required: null,
    trial_required: null,
    exclusion_coverage_limits: null,
    conservative_therapy_time: null,
    conservative_therapy_list: null,
    policy_url: documentUrl?.policyUrl
  };
  if (rowData[metaDataField]) {
    try {
      // Use the sanitizeJsonString utility function to sanitize the JSON string
      const sanitizedJson = sanitizeJsonString(rowData[metaDataField]);

      // Parse the sanitized JSON string
      const metaData = JSON.parse(sanitizedJson);

      const {
        diagnosis_codes,
        symptom_duration,
        pharma_required,
        trial_required,
        exclusion_coverage_limits,
        conservative_therapy_time,
        conservative_therapy_list,
      } = metaData;

      pdfData = {
        ...pdfData,
        diagnosis_codes,
        symptom_duration,
        pharma_required,
        trial_required,
        exclusion_coverage_limits,
        conservative_therapy_time,
        conservative_therapy_list,
      };
    } catch (error) {
      console.error('Failed to parse JSON:', error);
      // If parsing fails, use the data as is but with null values for the problematic fields
      // This prevents the entire function from failing
    }
  }

  if (rowData.contacts?.length > 0) {
    pdfData = {
      ...pdfData,
      payer_phone: rowData.contacts?.find((contact) => contact.type === 'paphone')?.contact,
      payer_fax: rowData.contacts?.find((contact) => contact.type === 'pafax')?.contact,
    };
  }
  return pdfData;
};

export const mapCoverageSummaryModalData = ({position, states, planType}) => {
  return {
    coverageSummaryModalData: {
      states,
      indications: position.indications,
      payer: position.payer,
      planType,
      criteria: position.criteria,
      policyUrl: position.policyUrl,
      stepEdit: position.stepEdit,
      stepEditText: position.stepEditText,
      coveredMedicalCodes: position.coveredMedicalCodes,
      priorAuthorizationText: position.priorAuthorizationText,
      initialdiagnosis: position.initialdiagnosis,
      initialagerequirement: position.initialagerequirement,
      prescriberRequirementsText: position.prescriberRequirementsText,
      monitoringRequirement: position.monitoringRequirement,
      coverageSummaryFields: [
        {id: 'Diagnosis', initialTherapy: position.initialdiagnosis, continuedTherapy: position.continueddiagnosis},
        {id: 'Step Requirement', initialTherapy: (position.stepEditText ? position.stepEditText : position.stepEdit.join(', ')), continuedTherapy: null},
        {id: 'Quantity Limit', initialTherapy: triStateDisplay(position.initialquantitylimit), continuedTherapy: triStateDisplay(position.continuedquantitylimit)},
        {id: 'Age requirement', initialTherapy: (position.initialagerequirement ? position.initialagerequirement : 'Unspecified'), continuedTherapy: (position.continuedagerequirement ? position.continuedagerequirement : 'Unspecified')},
        {id: 'Prescriber Requirements', initialTherapy: triStateDisplay(position.initialprescriberrequirements), continuedTherapy: triStateDisplay(position.continuedprescriberrequirements)},
        {id: '(Re)Authorization Duration', initialTherapy: (position.initialreauthorizationduration ? position.initialreauthorizationduration : 'Unspecified'), continuedTherapy: (position.continuedreauthorizationduration ? position.continuedreauthorizationduration : 'Unspecified')},
      ],
      verballyConfirmed: position.verballyConfirmed,
    }
  }
}